import React, { forwardRef, useRef, useEffect } from "react";
import { ROTARY_LOGO, RMB_LOGO, HAWA_MAHAL, SWOOSH, SAMPLE_PHOTO } from "./posterAssets";

const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Norican&family=Bebas+Neue&family=Oswald:wght@600;700&family=Inter:wght@400;500;700;800&family=Lora:ital,wght@1,600&display=swap";

export async function ensureFieldVisitFontsLoaded() {
  if (typeof document === "undefined") return;
  if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_URL;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  }
  if (document.fonts) {
    try {
      await document.fonts.ready;
      await Promise.allSettled([
        document.fonts.load("57px 'Norican'"),
        document.fonts.load("158px 'Bebas Neue'"),
        document.fonts.load("700 47px 'Oswald'"),
        document.fonts.load("700 26px 'Inter'"),
        document.fonts.load("800 78px 'Inter'"),
        document.fonts.load("italic 600 22px 'Lora'"),
      ]);
    } catch (e) {
      // font loading error ignored
    }
  }

  // Pre-cache inlined base64 Google fonts in background for instant, pixel-perfect download
  getInlinedFieldVisitFontsCss().catch(() => {});
}

let cachedInlinedFontCss = null;

export async function getInlinedFieldVisitFontsCss() {
  if (cachedInlinedFontCss) return cachedInlinedFontCss;
  try {
    const cssRes = await fetch(FONT_URL);
    let cssText = await cssRes.text();

    const fontUrls = (cssText.match(/https:\/\/fonts\.gstatic\.com\/[^\s"')]+/g) || []);
    const uniqueUrls = [...new Set(fontUrls)];

    const map = {};
    await Promise.all(
      uniqueUrls.map(async (url) => {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve("");
            reader.readAsDataURL(blob);
          });
          if (dataUrl && typeof dataUrl === "string") {
            map[url] = dataUrl;
          }
        } catch (e) {
          console.warn("Could not inline font binary:", url, e);
        }
      })
    );

    for (const [url, dataUrl] of Object.entries(map)) {
      if (dataUrl && dataUrl.startsWith("data:")) {
        cssText = cssText.split(url).join(dataUrl);
      }
    }

    cachedInlinedFontCss = cssText;
    return cssText;
  } catch (err) {
    console.warn("Could not fetch and inline Field Visit Google Fonts CSS:", err);
    return "";
  }
}

/**
 * Converts an image URL (including cross-origin member photos) to a base64 data URL
 * so that SVG export has zero external network dependencies.
 */
async function toDataUrl(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("data:")) return url;

  // Cache-busting query parameter prevents Chrome CORS cache poisoning from previous <img> tags
  const bustUrl = url.includes("?")
    ? `${url}&_t=${Date.now()}`
    : `${url}?_t=${Date.now()}`;

  // 1. Try fetch with CORS & cache-buster (fastest & most reliable for CORS-enabled APIs)
  try {
    const res = await fetch(bustUrl, { mode: "cors" });
    if (res.ok) {
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve("");
        reader.readAsDataURL(blob);
      });
      if (dataUrl && typeof dataUrl === "string" && dataUrl.startsWith("data:")) {
        return dataUrl;
      }
    }
  } catch (e) {
    // continue to fallback
  }

  // 2. Try Image with crossOrigin = "anonymous"
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = bustUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width || 200;
    canvas.height = img.naturalHeight || img.height || 200;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const canvasData = canvas.toDataURL("image/png");
    if (canvasData && canvasData.startsWith("data:")) {
      return canvasData;
    }
  } catch (err1) {
    // continue to fallback
  }

  // 3. Fallback to original url fetch
  try {
    const res = await fetch(url, { mode: "cors" });
    if (res.ok) {
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve("");
        reader.readAsDataURL(blob);
      });
      if (dataUrl && typeof dataUrl === "string" && dataUrl.startsWith("data:")) {
        return dataUrl;
      }
    }
  } catch (err2) {
    console.warn("Could not inline image URL to data URL:", url, err2);
  }

  return "";
}

/**
 * Pixel-perfect Canvas Exporter for FieldVisitPoster
 * Renders at 2x sharpness (2046 x 3074) matching on-screen poster 100%.
 *
 * Uses direct SVG rasterization with self-contained base64 fonts & images,
 * ensuring member photo displays, zero font distortion, and exact layout alignment.
 */
export async function generateFieldVisitPosterCanvas(element) {
  let el = element;
  if (!el) {
    el = document.querySelector(".field-visit-poster");
  }
  if (!el) {
    throw new Error("Field Visit poster element not found");
  }

  const svgEl = el.tagName && el.tagName.toLowerCase() === "svg" ? el : el.querySelector("svg");
  if (!svgEl) {
    throw new Error("SVG element not found inside Field Visit poster");
  }

  // Small delay to ensure any live image rendering or state update has painted
  await new Promise((r) => setTimeout(r, 60));

  try {
    const svgClone = svgEl.cloneNode(true);
    svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgClone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    svgClone.setAttribute("width", "1023");
    svgClone.setAttribute("height", "1537");
    svgClone.setAttribute("viewBox", "0 0 1023 1537");

    // 1. Inline all external images (member photo, logos, skyline, etc.) to base64
    const images = svgClone.querySelectorAll("image");
    await Promise.all(
      Array.from(images).map(async (img) => {
        const href = img.getAttribute("href") || img.getAttribute("xlink:href");
        if (href && !href.startsWith("data:")) {
          const dataUrl = await toDataUrl(href);
          if (dataUrl && dataUrl.startsWith("data:")) {
            img.setAttribute("href", dataUrl);
            img.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", dataUrl);
          } else {
            // Fallback to SAMPLE_PHOTO if member photo fetch failed so photo is never blank
            img.setAttribute("href", SAMPLE_PHOTO);
            img.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", SAMPLE_PHOTO);
          }
        }
      })
    );

    // 2. Fetch and embed true base64 Google Fonts
    const fontCss = await getInlinedFieldVisitFontsCss();
    let defs = svgClone.querySelector("defs");
    if (!defs) {
      defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
      svgClone.insertBefore(defs, svgClone.firstChild);
    }
    let styleEl = defs.querySelector("style");
    if (!styleEl) {
      styleEl = document.createElementNS("http://www.w3.org/2000/svg", "style");
      defs.appendChild(styleEl);
    }
    if (fontCss) {
      styleEl.textContent = fontCss;
    }

    // Ensure clipPath is present inside defs
    if (!defs.querySelector("#fv-photo-clip")) {
      const clipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
      clipPath.setAttribute("id", "fv-photo-clip");
      const pathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
      pathEl.setAttribute("d", PHOTO_CLIP);
      clipPath.appendChild(pathEl);
      defs.appendChild(clipPath);
    }

    // 3. Serialize self-contained SVG to Blob URL
    const svgString = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const blobUrl = URL.createObjectURL(svgBlob);

    // 4. Render to full 2x high-resolution canvas (2046 x 3074) matching on-screen poster 100%
    const targetW = 2046;
    const targetH = 3074;
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");

    // Navy background
    ctx.fillStyle = C.navy;
    ctx.fillRect(0, 0, targetW, targetH);

    const img = new Image();
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = (e) => rej(new Error("Failed to rasterize Field Visit SVG to canvas"));
      img.src = blobUrl;
    });

    ctx.drawImage(img, 0, 0, targetW, targetH);
    URL.revokeObjectURL(blobUrl);

    return canvas;
  } catch (err) {
    console.warn("Direct SVG rasterization failed:", err);
    throw err;
  }
}

export async function downloadFieldVisitPoster(elementOrProps, filename) {
  const canvas = await generateFieldVisitPosterCanvas(elementOrProps);
  const dataUrl = canvas.toDataURL("image/png");
  const link = document.createElement("a");
  link.download = filename || "RMB_Field_Visit_Poster.png";
  link.href = dataUrl;
  link.click();
  return dataUrl;
}

/**
 * RMB Jaipur – "Field Visit" invitation poster
 *
 * <FieldVisitPoster
 *   photo="/members/priya.jpg"
 *   data={{ hostName: "PRIYA GUPTA", date: "5th", ... }}   // any field is optional
 * />
 *
 * Props
 *   data        object – text content, see DEFAULT_DATA below (merged over the defaults)
 *   photo       host photo URL (shown inside the curved gold-outlined shape)
 *   rotaryLogo  optional logo URL         rmbLogo   optional logo URL
 *   skyline     optional Hawa Mahal artwork URL
 *   className   optional wrapper class
 *
 * The poster is drawn on a 1023 x 1537 canvas (the reference size) inside one SVG with a
 * viewBox, so it scales perfectly to any container width and keeps the 1023:1537 ratio.
 * The ref is forwarded to the wrapper div (handy for PNG export with html-to-image).
 */

export const DEFAULT_DATA = {
  invitation: "You are Invited to",
  title: "FIELD VISIT",
  at: "at",
  companyName: "SMART SOLUTIONS",
  companySub: "INDIA PVT. LTD.",
  categoryLabel: "Category",
  category: "MOBILE & GADGETS",
  hostedByLabel: "Hosted by",
  hostName: "PRIYA GUPTA",
  hostPhone: "9828888857",
  day: "SATURDAY",
  date: "5th",
  monthYear: "SEPTEMBER 2026",
  timeFrom: "11:00am",
  timeTo: "1:00pm",
  addressLabel: "ADDRESS:",
  address: ["Smart Solutions,", "D-34, Near SBI Bank,", "GT Road, Malviya Nagar,", "Jaipur, Rajasthan – 302017"],
  contactPhone: "+91 9828888857",
  tagline: ["Let’s Connect", "Understand", "Grow Together"],
  committee: [
    { name: "Sidharth Makharia", role: "Chairman" },
    { name: "Gaurav Mathur", role: "Vice Chairman" },
    { name: "Dr. Megha Sharma", role: "Treasurer" },
    { name: "Ashish Bhardwaj", role: "Secretary" },
  ],
  website: "rmbjaipur.com",
  instagram: "rotarymeansbusinessjaipur",
};

/* ---------- design tokens (sampled from the reference) ---------- */
const C = {
  navy: "#02122b",
  ink: "#0a1223",
  footer: "#010f2a",
  gold: "#e8a53a",
  goldDeep: "#e89e17",
  goldBar: "#eb9a0d",
  rule: "#e3ae4f",
  card: "#f8f8f8",
  cardLine: "#dcdcdc",
  white: "#ffffff",
  header: "#fcfcfc",
};
const F = {
  script: "'Norican', cursive",
  display: "'Bebas Neue', 'Oswald', sans-serif",
  cond: "'Oswald', 'Arial Narrow', sans-serif",
  sans: "'Inter', Arial, sans-serif",
  serif: "'Lora', Georgia, serif",
};

/* outline of the curved photo frame (centre line of the gold stroke) */
const PHOTO_CURVE =
  "M668 206C663.0 209.7 647.0 220.7 638 228C629.0 235.3 620.5 242.7 614 250C607.5 257.3 603.7 264.7 599 272C594.3 279.3 589.8 286.7 586 294C582.2 301.3 579.0 308.7 576 316C573.0 323.3 570.2 330.7 568 338C565.8 345.3 564.3 352.7 563 360C561.7 367.3 560.7 374.7 560 382C559.3 389.3 559.0 396.7 559 404C559.0 411.3 559.2 418.7 560 426C560.8 433.3 562.3 440.7 564 448C565.7 455.3 568.0 462.7 570 470C572.0 477.3 573.8 484.7 576 492C578.2 499.3 580.8 506.7 583 514C585.2 521.3 587.0 528.7 589 536C591.0 543.3 593.0 550.7 595 558C597.0 565.3 599.0 572.7 601 580C603.0 587.3 605.0 594.7 607 602C609.0 609.3 611.0 616.7 613 624C615.0 631.3 617.2 638.7 619 646C620.8 653.3 622.2 660.7 624 668C625.8 675.3 628.2 682.7 630 690C631.8 697.3 633.2 704.7 635 712C636.8 719.3 639.2 726.7 641 734C642.8 741.3 644.2 748.7 646 756C647.8 763.3 650.0 770.7 652 778C654.0 785.3 656.2 792.7 658 800C659.8 807.3 661.2 814.7 663 822C664.8 829.3 667.2 836.7 669 844C670.8 851.3 672.2 858.7 674 866C675.8 873.3 678.0 880.7 680 888C682.0 895.3 683.7 902.7 686 910C688.3 917.3 690.5 924.7 694 932C697.5 939.3 701.5 946.7 707 954C712.5 961.3 718.2 969.0 727 976C735.8 983.0 751.2 991.5 760 996C768.8 1000.5 768.3 1000.3 780 1003C791.7 1005.7 810.0 1010.8 830 1012C850.0 1013.2 880.0 1011.0 900 1010C920.0 1009.0 933.3 1007.3 950 1006C966.7 1004.7 987.8 1002.8 1000 1002C1012.2 1001.2 1019.2 1001.2 1023 1001";
/* gold plate behind the company name: its right edge slants to follow the photo curve */
const PLATE = "M38 528H562Q578 528 582 544L606 668Q609 690 588 690H38Q16 690 16 668V550Q16 528 38 528Z";
const PHOTO_CLIP = `${PHOTO_CURVE}L1023 206Z`;

/* ---------- small building blocks ---------- */

const Rule = ({ x1, x2, y, color = C.rule }) => (
  <rect x={x1} y={y - 1.25} width={x2 - x1} height="2.5" rx="1.25" fill={color} />
);

/** Text with a stroke of the same colour – thickens single-weight display fonts. */
const Heavy = ({ stroke = 1.6, fill, children, ...rest }) => (
  <text fill={fill} stroke={fill} strokeWidth={stroke} strokeLinejoin="round" {...rest}>
    {children}
  </text>
);

/** "11:00am" -> big "11:00" + smaller "am" */
function TimeText({ value, x, y, size, small, weight = 800, prefix }) {
  const m = /^(.*?)(am|pm)$/i.exec(value.trim());
  const main = m ? m[1] : value;
  const suffix = m ? m[2] : "";
  return (
    <text x={x} y={y} fontFamily={F.sans} fontWeight={weight} fontSize={size} letterSpacing="-1" fill={C.ink}>
      {prefix}
      {main}
      {suffix && (
        <tspan fontSize={small} fontWeight="700" letterSpacing="0">
          {suffix}
        </tspan>
      )}
    </text>
  );
}

/* ---------- icons (all drawn in poster coordinates) ---------- */

const CalendarIcon = ({ cx, cy }) => (
  <g transform={`translate(${cx - 34} ${cy - 36})`} fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="10" width="64" height="62" rx="9" />
    <line x1="2" y1="29" x2="66" y2="29" />
    <line x1="19" y1="2" x2="19" y2="18" />
    <line x1="49" y1="2" x2="49" y2="18" />
    <g fill="#fff" stroke="none">
      {[0, 1, 2].map((r) =>
        [0, 1, 2].map((c) => <rect key={`${r}${c}`} x={15 + c * 15} y={36 + r * 12} width="10" height="8" rx="1.5" />)
      )}
    </g>
  </g>
);

const ClockIcon = ({ cx, cy }) => {
  const ticks = Array.from({ length: 12 }, (_, i) => i * 30);
  return (
    <g>
      <circle cx={cx} cy={cy} r="54" fill="#fff" stroke={C.ink} strokeWidth="11" />
      {ticks.map((a) => (
        <line
          key={a}
          x1={cx}
          y1={cy - 44}
          x2={cx}
          y2={cy - (a % 90 === 0 ? 35 : 39)}
          stroke={C.ink}
          strokeWidth={a % 90 === 0 ? 4 : 2.5}
          strokeLinecap="round"
          transform={`rotate(${a} ${cx} ${cy})`}
        />
      ))}
      <line x1={cx} y1={cy} x2={cx} y2={cy - 33} stroke={C.ink} strokeWidth="4.5" strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={cx + 15} y2={cy + 24} stroke={C.ink} strokeWidth="4.5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="4" fill={C.ink} />
    </g>
  );
};

const PinIcon = ({ cx, cy }) => (
  <g>
    <path
      d={`M${cx} ${cy + 27} C${cx - 7} ${cy + 15} ${cx - 23} ${cy + 1} ${cx - 23} ${cy - 15} A23 23 0 0 1 ${cx + 23} ${cy - 15} C${cx + 23} ${cy + 1} ${cx + 7} ${cy + 15} ${cx} ${cy + 27}Z`}
      fill={C.gold}
    />
    <circle cx={cx} cy={cy - 15} r="8.5" fill={C.ink} />
    <ellipse cx={cx} cy={cy + 33} rx="30" ry="9" fill="none" stroke={C.gold} strokeWidth="4" />
  </g>
);

const PhoneGlyph = ({ cx, cy, size, color }) => (
  <svg x={cx - size / 2} y={cy - size / 2} width={size} height={size} viewBox="0 0 24 24">
    <path
      fill={color}
      d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
    />
  </svg>
);

const UserIcon = ({ cx, cy }) => (
  <g>
    <circle cx={cx} cy={cy} r="22" fill={C.footer} stroke={C.gold} strokeWidth="2.5" />
    <circle cx={cx} cy={cy - 6} r="6.5" fill={C.gold} />
    <path d={`M${cx - 12} ${cy + 13} C${cx - 12} ${cy + 2} ${cx + 12} ${cy + 2} ${cx + 12} ${cy + 13}Z`} fill={C.gold} />
  </g>
);

const MobileIcon = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`} fill="none" stroke={C.ink} strokeLinecap="round">
    <rect x="2.5" y="2.5" width="33" height="57" rx="7" strokeWidth="5" />
    <line x1="14" y1="9" x2="24" y2="9" strokeWidth="3" />
    <circle cx="19" cy="52" r="1.6" fill={C.ink} strokeWidth="2" />
  </g>
);

const GlobeIcon = ({ cx, cy }) => (
  <g fill="none" stroke={C.ink} strokeWidth="2.2">
    <circle cx={cx} cy={cy} r="11" />
    <ellipse cx={cx} cy={cy} rx="4.6" ry="11" />
    <line x1={cx - 11} y1={cy} x2={cx + 11} y2={cy} />
    <path d={`M${cx - 9} ${cy - 5.5}H${cx + 9}M${cx - 9} ${cy + 5.5}H${cx + 9}`} />
  </g>
);

const InstagramIcon = ({ cx, cy }) => (
  <g fill="none" stroke={C.ink} strokeWidth="2.4">
    <rect x={cx - 11.5} y={cy - 11.5} width="23" height="23" rx="6.5" />
    <circle cx={cx} cy={cy} r="5.4" />
    <circle cx={cx + 6.6} cy={cy - 6.6} r="1" fill={C.ink} />
  </g>
);

/* ---------- sections ---------- */

function Header({ rotaryLogo, rmbLogo, d }) {
  return (
    <g>
      <rect x="10" y="20" width="1003" height="208" rx="32" fill={C.header} />
      <image href={rotaryLogo} x="40" y="28" width="442" height="186" />
      <rect x="505" y="48" width="2.6" height="158" fill="#1b1b1b" />
      <image href={rmbLogo} x="528" y="28" width="474" height="186" />
    </g>
  );
}

function PhotoFrame({ photo }) {
  return (
    <g>
      <image href={SWOOSH} x="585" y="700" width="215" height="322" />
      <clipPath id="fv-photo-clip">
        <path d={PHOTO_CLIP} />
      </clipPath>
      <rect x="520" y="200" width="503" height="820" fill="#e9e1d6" clipPath="url(#fv-photo-clip)" />
      <image
        href={photo}
        x="520"
        y="228"
        width="503"
        height="784"
        preserveAspectRatio="xMidYMin slice"
        clipPath="url(#fv-photo-clip)"
      />
      <path d={PHOTO_CURVE} fill="none" stroke={C.goldDeep} strokeWidth="8" strokeLinecap="round" />
    </g>
  );
}

function CategoryText({ category }) {
  const cat = (category || "").trim();
  if (!cat) return null;

  const maxW = 415;
  const len = cat.length;

  const hasNewline = cat.includes("\n");
  const words = cat.split(/\s+/);
  const shouldWrap = hasNewline || (len > 24 && words.length >= 2);

  if (shouldWrap) {
    let lines = [];
    if (hasNewline) {
      lines = cat.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 2);
    } else {
      let bestSplit = 1;
      let bestScore = Infinity;
      for (let i = 1; i < words.length; i++) {
        const l1 = words.slice(0, i).join(" ");
        const l2 = words.slice(i).join(" ");
        const maxL = Math.max(l1.length, l2.length);
        const diff = Math.abs(l1.length - l2.length);
        const score = maxL * 10 + diff;
        if (score < bestScore) {
          bestScore = score;
          bestSplit = i;
        }
      }
      lines = [words.slice(0, bestSplit).join(" "), words.slice(bestSplit).join(" ")];
    }

    const maxLineLen = Math.max(lines[0].length, lines[1].length);
    const fontSize = maxLineLen > 24 ? 20 : maxLineLen > 18 ? 23 : 26;
    const letterSpacing = fontSize >= 24 ? 1.5 : 1.0;

    return (
      <g>
        <text
          x="149"
          y="785"
          fontFamily={F.cond}
          fontWeight="700"
          fontSize={fontSize}
          letterSpacing={letterSpacing}
          fill={C.ink}
          {...(lines[0].length * fontSize * 0.55 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
        >
          {lines[0]}
        </text>
        <text
          x="149"
          y="815"
          fontFamily={F.cond}
          fontWeight="700"
          fontSize={fontSize}
          letterSpacing={letterSpacing}
          fill={C.ink}
          {...(lines[1].length * fontSize * 0.55 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
        >
          {lines[1]}
        </text>
      </g>
    );
  }

  // Single line auto-scaling
  const fontSize = len > 22 ? 28 : len > 18 ? 32 : len > 14 ? 37 : 42;
  const letterSpacing = fontSize >= 37 ? 2.5 : fontSize >= 32 ? 1.8 : 1.2;
  const estWidth = len * (fontSize * 0.52 + letterSpacing);
  const shouldClamp = estWidth > maxW;

  return (
    <text
      x="149"
      y="808"
      fontFamily={F.cond}
      fontWeight="700"
      fontSize={fontSize}
      letterSpacing={letterSpacing}
      fill={C.ink}
      {...(shouldClamp ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
    >
      {cat}
    </text>
  );
}

function CompanyPlateText({ companyName, companySub }) {
  const compName = (companyName || "").trim();
  const compSub = (companySub || "").trim();

  if (!compName && !compSub) return null;

  const maxW = 490; // Safe inner width inside the gold plate
  const cx = 305;   // Horizontal center of the gold plate

  // Helper for balancing words across 2 lines
  const splitInto2Balanced = (words) => {
    let bestDiff = Infinity;
    let bestSplit = 1;
    for (let i = 1; i < words.length; i++) {
      const l1 = words.slice(0, i).join(" ");
      const l2 = words.slice(i).join(" ");
      const diff = Math.abs(l1.length - l2.length);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestSplit = i;
      }
    }
    return [words.slice(0, bestSplit).join(" "), words.slice(bestSplit).join(" ")];
  };

  // Helper for balancing words across 3 lines
  const splitInto3Balanced = (words) => {
    let bestScore = Infinity;
    let best = [words.slice(0, 1).join(" "), words.slice(1, 2).join(" "), words.slice(2).join(" ")];
    for (let i = 1; i < words.length - 1; i++) {
      for (let j = i + 1; j < words.length; j++) {
        const l1 = words.slice(0, i).join(" ");
        const l2 = words.slice(i, j).join(" ");
        const l3 = words.slice(j).join(" ");
        const score = Math.max(l1.length, l2.length, l3.length) - Math.min(l1.length, l2.length, l3.length);
        if (score < bestScore) {
          bestScore = score;
          best = [l1, l2, l3];
        }
      }
    }
    return best;
  };

  // Case 1: Both companyName and companySub exist
  if (compSub) {
    // If companyName is very long (> 22 chars with multiple words), wrap into 2 lines + compSub
    if (compName.length > 22 && compName.includes(" ")) {
      const words = compName.split(/\s+/).filter(Boolean);
      const [line1, line2] = splitInto2Balanced(words);
      const maxL = Math.max(line1.length, line2.length);
      const fsTop = maxL > 22 ? 40 : maxL > 16 ? 46 : 52;
      const fsSub = compSub.length > 22 ? 34 : compSub.length > 16 ? 38 : 44;

      return (
        <g>
          <Heavy
            x={cx}
            y="575"
            textAnchor="middle"
            fontFamily={F.display}
            fontSize={fsTop}
            fill="#07112b"
            stroke={0.7}
            {...(line1.length * fsTop * 0.52 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
          >
            {line1}
          </Heavy>
          <Heavy
            x={cx}
            y="622"
            textAnchor="middle"
            fontFamily={F.display}
            fontSize={fsTop}
            fill="#07112b"
            stroke={0.7}
            {...(line2.length * fsTop * 0.52 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
          >
            {line2}
          </Heavy>
          <text
            x={cx}
            y="670"
            textAnchor="middle"
            fontFamily={F.cond}
            fontWeight="700"
            fontSize={fsSub}
            letterSpacing={fsSub >= 40 ? 3.0 : 1.8}
            fill="#07112b"
            {...(compSub.length * (fsSub * 0.52 + 2) > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
          >
            {compSub}
          </text>
        </g>
      );
    }

    // Standard 2 lines: line 1 = compName (display font), line 2 = compSub (condensed tracking font)
    const fsName = compName.length > 18 ? 64 : compName.length > 13 ? 74 : 84;
    const fsSub = compSub.length > 24 ? 36 : compSub.length > 16 ? 44 : 50;
    const estWName = compName.length * fsName * 0.52;
    const estWSub = compSub.length * (fsSub * 0.52 + 3);

    return (
      <g>
        <Heavy
          x={cx}
          y="610"
          textAnchor="middle"
          fontFamily={F.display}
          fontSize={fsName}
          fill="#07112b"
          stroke={0.9}
          {...(estWName > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
        >
          {compName}
        </Heavy>
        <text
          x={cx}
          y="668"
          textAnchor="middle"
          fontFamily={F.cond}
          fontWeight="700"
          fontSize={fsSub}
          letterSpacing={fsSub >= 44 ? 3.4 : 2.0}
          fill="#07112b"
          {...(estWSub > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
        >
          {compSub}
        </text>
      </g>
    );
  }

  // Case 2: Only companyName is provided (companySub is empty)
  let rawLines = [];
  if (compName.includes("\n")) {
    rawLines = compName.split("\n").map((l) => l.trim()).filter(Boolean);
  } else if (compName.length <= 20 || !compName.includes(" ")) {
    rawLines = [compName];
  } else if (compName.length <= 42) {
    const words = compName.split(/\s+/).filter(Boolean);
    rawLines = splitInto2Balanced(words);
  } else {
    const words = compName.split(/\s+/).filter(Boolean);
    rawLines = splitInto3Balanced(words);
  }

  // Single line
  if (rawLines.length === 1) {
    const single = rawLines[0];
    const fs = single.length > 18 ? 64 : single.length > 14 ? 74 : single.length > 10 ? 82 : 90;
    const estW = single.length * fs * 0.52;
    return (
      <Heavy
        x={cx}
        y="622"
        textAnchor="middle"
        fontFamily={F.display}
        fontSize={fs}
        fill="#07112b"
        stroke={1.0}
        {...(estW > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
      >
        {single}
      </Heavy>
    );
  }

  // 2 lines
  if (rawLines.length === 2) {
    const [l1, l2] = rawLines;
    const maxL = Math.max(l1.length, l2.length);
    const fs = maxL > 22 ? 48 : maxL > 17 ? 55 : maxL > 13 ? 62 : 68;
    const estW1 = l1.length * fs * 0.52;
    const estW2 = l2.length * fs * 0.52;

    return (
      <g>
        <Heavy
          x={cx}
          y="598"
          textAnchor="middle"
          fontFamily={F.display}
          fontSize={fs}
          fill="#07112b"
          stroke={0.8}
          {...(estW1 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
        >
          {l1}
        </Heavy>
        <Heavy
          x={cx}
          y="658"
          textAnchor="middle"
          fontFamily={F.display}
          fontSize={fs}
          fill="#07112b"
          stroke={0.8}
          {...(estW2 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
        >
          {l2}
        </Heavy>
      </g>
    );
  }

  // 3 lines
  const [l1, l2, l3] = rawLines;
  const maxL = Math.max(l1.length, l2.length, l3.length);
  const fs = maxL > 22 ? 36 : maxL > 17 ? 40 : 45;
  const estW1 = l1.length * fs * 0.52;
  const estW2 = l2.length * fs * 0.52;
  const estW3 = l3.length * fs * 0.52;

  return (
    <g>
      <Heavy
        x={cx}
        y="574"
        textAnchor="middle"
        fontFamily={F.display}
        fontSize={fs}
        fill="#07112b"
        stroke={0.6}
        {...(estW1 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
      >
        {l1}
      </Heavy>
      <Heavy
        x={cx}
        y="622"
        textAnchor="middle"
        fontFamily={F.display}
        fontSize={fs}
        fill="#07112b"
        stroke={0.6}
        {...(estW2 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
      >
        {l2}
      </Heavy>
      <Heavy
        x={cx}
        y="670"
        textAnchor="middle"
        fontFamily={F.display}
        fontSize={fs}
        fill="#07112b"
        stroke={0.6}
        {...(estW3 > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
      >
        {l3}
      </Heavy>
    </g>
  );
}

function Invitation({ d }) {
  const hostName = (d.hostName || "").trim();
  const hostFontSize = hostName.length > 22 ? 46 : (hostName.length > 15 ? 54 : 62);

  return (
    <g>
      <Rule x1="26" x2="68" y="290" />
      <Rule x1="492" x2="537" y="290" />
      <text x="277" y="305" textAnchor="middle" fontFamily={F.script} fontSize="57" fill={C.gold}>
        {d.invitation}
      </text>

      <Heavy x="283" y="456" textAnchor="middle" fontFamily={F.display} fontSize="158" fill="#fff" stroke={1.1}>
        {d.title}
      </Heavy>

      <Rule x1="26" x2="240" y="497" />
      <Rule x1="331" x2="538" y="497" />
      <text x="284" y="507" textAnchor="middle" fontFamily={F.script} fontSize="56" fill={C.gold}>
        {d.at}
      </text>

      {/* company plate */}
      <defs>
        <linearGradient id="fv-plate" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f4ac37" />
          <stop offset="0.22" stopColor="#f8c553" />
          <stop offset="0.55" stopColor="#fbd066" />
          <stop offset="1" stopColor="#f2b040" />
        </linearGradient>
        <linearGradient id="fv-plate-shade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.45" stopColor="#d98a12" stopOpacity="0" />
          <stop offset="1" stopColor="#d98a12" stopOpacity="0.38" />
        </linearGradient>
      </defs>
      <path id="fv-plate-shape" d={PLATE} fill="url(#fv-plate)" />
      <path d={PLATE} fill="url(#fv-plate-shade)" />
      <CompanyPlateText companyName={d.companyName} companySub={d.companySub} />

      <Rule x1="77" x2="213" y="726" />
      <Rule x1="405" x2="545" y="726" />
      <text x="305" y="737" textAnchor="middle" fontFamily={F.script} fontSize="45" fill={C.gold}>
        {d.categoryLabel}
      </text>

      {/* category pill */}
      <rect x="56" y="754" width="526" height="78" rx="17" fill="#fafafa" />
      <MobileIcon x="82" y="762" />
      <CategoryText category={d.category} />

      <Rule x1="97" x2="208" y="867" />
      <Rule x1="410" x2="530" y="867" />
      <text x="308" y="879" textAnchor="middle" fontFamily={F.script} fontSize="43" fill={C.gold}>
        {d.hostedByLabel}
      </text>

      <text x="298" y="954" textAnchor="middle" fontFamily={F.cond} fontWeight="700" fontSize={hostFontSize} letterSpacing="1.4" fill="#fff">
        {hostName}
      </text>
      {(() => {
        const rawPhone = (d.hostPhone ?? d.contactPhone ?? "").toString().trim();
        const hostPhoneDisplay = rawPhone.replace(/^\+?91[\s-]*/, '').replace(/^0/, '').trim();
        if (!hostPhoneDisplay) return null;
        const phoneFontSize = hostPhoneDisplay.length > 12 ? 30 : (hostPhoneDisplay.length > 10 ? 33 : 38.5);
        return (
          <>
            <circle cx="157" cy="988" r="5.5" fill={C.goldDeep} />
            <circle cx="449" cy="988" r="5.5" fill={C.goldDeep} />
            <text x="305" y="1002" textAnchor="middle" fontFamily={F.sans} fontWeight="800" fontSize={phoneFontSize} fill={C.goldDeep}>
              {hostPhoneDisplay}
            </text>
          </>
        );
      })()}
    </g>
  );
}

const InfoCard = ({ x, y, w, h }) => (
  <rect x={x} y={y} width={w} height={h} rx="28" fill={C.card} stroke={C.cardLine} strokeWidth="1.8" />
);

function InfoSection({ d, skyline }) {
  const addressLines = Array.isArray(d.address)
    ? d.address
    : (typeof d.address === "string" ? d.address.split(/,|\n/).map(s => s.trim()).filter(Boolean) : DEFAULT_DATA.address);

  const taglines = Array.isArray(d.tagline) ? d.tagline : DEFAULT_DATA.tagline;

  return (
    <g>
      <rect x="16" y="1021" width="990" height="480" rx="30" fill={C.card} />

      {/* date */}
      <InfoCard x="33" y="1037" w="494" h="153" />
      <circle cx="128" cy="1106" r="64" fill={C.ink} />
      <CalendarIcon cx={128} cy={1106} />
      <text x="339" y="1070" textAnchor="middle" fontFamily={F.sans} fontWeight="700" fontSize="26.5" fill={C.ink}>
        {d.day}
      </text>
      <text x="347" y="1145" textAnchor="middle" fontFamily={F.sans} fontWeight="800" fontSize="78" fill={C.goldDeep}>
        {d.date}
      </text>
      <text x="346" y="1177" textAnchor="middle" fontFamily={F.sans} fontWeight="700" fontSize="26" fill={C.ink}>
        {d.monthYear}
      </text>

      {/* time */}
      <InfoCard x="547" y="1037" w="435" h="153" />
      <ClockIcon cx={630} cy={1106} />
      <TimeText value={d.timeFrom} x={724} y={1103} size={48} small={34} />
      <TimeText value={d.timeTo} x={714} y={1152} size={44} small={34} prefix="To " />
      <Rule x1="718" x2="931" y="1176" color="#e0b560" />

      {/* address */}
      <InfoCard x="33" y="1202" w="949" h="155" />
      <circle cx="105" cy="1280" r="60" fill={C.ink} />
      <PinIcon cx={105} cy={1278} />
      <text x="199" y="1235" fontFamily={F.sans} fontWeight="700" fontSize="27.5" fill={C.ink}>
        {d.addressLabel}
      </text>
      {addressLines.map((line, i) => (
        <text key={i} x="199" y={1262 + i * 28} fontFamily={F.sans} fontWeight="500" fontSize="21" fill={C.ink}>
          {line}
        </text>
      ))}
      <image href={skyline} x="594" y="1195" width="378" height="154" />

      {/* contact + tagline */}
      <Rule x1="77" x2="290" y="1391" />
      <Rule x1="725" x2="932" y="1391" />
      <circle cx="353" cy="1389" r="21" fill={C.ink} />
      <PhoneGlyph cx={353} cy={1389} size={22} color={C.gold} />
      <text x="389" y="1402" fontFamily={F.sans} fontWeight="700" fontSize="37" fill={C.ink}>
        {d.contactPhone}
      </text>

      <Rule x1="77" x2="250" y="1423" />
      <Rule x1="770" x2="932" y="1423" />
      {[341.5, 508, 677.5].map((x, i) => (
        <text key={i} x={x} y={1431} textAnchor="middle" fontFamily={F.serif} fontStyle="italic" fontWeight="600" fontSize="22.5" fill={C.ink}>
          {taglines[i] || ""}
        </text>
      ))}
      {[430, 586].map((x) => (
        <circle key={x} cx={x} cy="1424" r="5" fill={C.goldDeep} />
      ))}
    </g>
  );
}

function Footer({ d }) {
  const committeeList = Array.isArray(d.committee) ? d.committee : DEFAULT_DATA.committee;
  const cols = [
    { icon: 72, text: 107 },
    { icon: 322, text: 357 },
    { icon: 554, text: 589 },
    { icon: 802, text: 838 },
  ];
  return (
    <g>
      {/* runs under the gold bar so only the top corners show as rounded */}
      <rect x="28" y="1441" width="967" height="70" rx="16" fill={C.footer} />
      {committeeList.slice(0, 4).map((m, i) => (
        <g key={i}>
          <UserIcon cx={cols[i].icon} cy={1470} />
          <text x={cols[i].text} y="1466" fontFamily={F.sans} fontWeight="500" fontSize="17" fill={C.gold}>
            {m.name}
          </text>
          <text x={cols[i].text} y="1488" fontFamily={F.sans} fontWeight="400" fontSize="17" fill="#e5e5f1">
            {m.role}
          </text>
        </g>
      ))}
      {[277, 508, 760].map((x) => (
        <rect key={x} x={x} y="1452" width="1.6" height="38" fill="#fff" opacity="0.55" />
      ))}

      <rect x="0" y="1500" width="1023" height="37" fill={C.goldBar} />
      <GlobeIcon cx={250} cy={1519} />
      <text x="273" y="1526" fontFamily={F.sans} fontWeight="500" fontSize="21.5" fill={C.ink}>
        {d.website}
      </text>
      <rect x="462" y="1507" width="2" height="24" fill={C.ink} />
      <InstagramIcon cx={515} cy={1519} />
      <text x="546" y="1526" fontFamily={F.sans} fontWeight="500" fontSize="20.5" fill={C.ink}>
        {d.instagram}
      </text>
    </g>
  );
}

/* ---------- poster ---------- */

const FieldVisitPoster = forwardRef(function FieldVisitPoster(
  { data, photo, rotaryLogo = ROTARY_LOGO, rmbLogo = RMB_LOGO, skyline = HAWA_MAHAL, className = "", style = {} },
  ref
) {
  const d = { ...DEFAULT_DATA, ...data };
  const finalPhoto = photo || SAMPLE_PHOTO;
  const containerRef = useRef(null);

  useEffect(() => {
    ensureFieldVisitFontsLoaded();
  }, []);

  // Attach canvas generator directly to ref DOM node so callers can call getPosterCanvas()
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    el.getPosterCanvas = () => generateFieldVisitPosterCanvas(el);
    el.getPosterDataUrl = async () => {
      const c = await el.getPosterCanvas();
      return c.toDataURL("image/png");
    };
  }, [d, finalPhoto, rotaryLogo, rmbLogo, skyline]);

  const setCombinedRef = (node) => {
    containerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  return (
    <>
      <style>{`
        @import url('${FONT_URL}');
      `}</style>
      <div
        ref={setCombinedRef}
        className={`field-visit-poster relative overflow-hidden select-none ${className}`}
        style={{ width: "100%", maxWidth: 1023, aspectRatio: "1023 / 1537", margin: "0 auto", background: C.navy, ...style }}
      >
        <svg
          viewBox="0 0 1023 1537"
          width="100%"
          height="100%"
          role="img"
          aria-label={`${d.title} at ${d.companyName} ${d.companySub}, hosted by ${d.hostName}. ${d.day} ${d.date} ${d.monthYear}, ${d.timeFrom} to ${d.timeTo}.`}
          style={{ display: "block" }}
        >
          <defs>
            <clipPath id="fv-photo-clip">
              <path d={PHOTO_CLIP} />
            </clipPath>
          </defs>
          <rect width="1023" height="1537" fill={C.navy} />
          <PhotoFrame photo={finalPhoto} />
          <Invitation d={d} />
          <Header rotaryLogo={rotaryLogo} rmbLogo={rmbLogo} d={d} />
          <InfoSection d={d} skyline={skyline} />
          <Footer d={d} />
        </svg>
      </div>
    </>
  );
});

export default FieldVisitPoster;
